/** UXP-00009 — Supply Chain Platform framework (mirrors pkg/supplychain) */

export const UXP_SC_ID = 'UXP-00009';

export interface CommandCenterKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface ForecastRow {
  horizon: string;
  units: number;
  change: string;
}

export interface ReplenishmentRow {
  sku: string;
  qty: number;
  daysLeft: number;
  supplier: string;
  roi: string;
}

export interface PurchaseOrderRow {
  po: string;
  supplier: string;
  items: number;
  value: string;
  status: string;
}

export interface FBASnapshotRow {
  metric: string;
  value: string;
  alert: boolean;
}

export interface WarehouseRow {
  name: string;
  capacity: number;
  used: number;
  inbound: number;
  outbound: number;
}

export interface OptimizationRec {
  type: string;
  title: string;
  impact: string;
  confidence: number;
}

export interface CashFlowRow {
  label: string;
  current: string;
  forecast: string;
}

export const COMMAND_CENTER_METRICS = [
  'Inventory Health', 'Available Stock', 'Reserved Stock', 'Inbound', 'Transfer',
  'Damaged', 'Aging', 'Inventory Value', 'Working Capital', 'Days of Cover', 'Storage Fees',
];

export const FORECAST_HORIZONS = ['7', '15', '30', '60', '90', '180', '365 Days'];

export const FORECAST_INPUTS = [
  'Seasonality', 'Advertising', 'Promotions', 'Marketplace Events',
  'Growth DNA', 'Customer Intent DNA', 'Historical Sales',
];

export const REPLENISHMENT_FEATURES = [
  'Reorder Timing', 'Reorder Quantity', 'Safety Stock', 'Lead Time',
  'Supplier', 'Expected Stockout', 'Expected ROI',
];

export const PROCUREMENT_FEATURES = [
  'Suppliers', 'Purchase Orders', 'Lead Times', 'MOQ', 'Pricing', 'Quality', 'Payment Terms', 'Approvals',
];

export const SUPPLIER_DIMENSIONS = [
  'Supplier Performance', 'Reliability', 'Quality', 'Delivery Accuracy',
  'Pricing Trends', 'Risk', 'Alternative Suppliers',
];

export const MANUFACTURING_FEATURES = [
  'Production', 'Capacity', 'Raw Materials', 'Production Queue', 'Expected Completion', 'Bottlenecks',
];

export const SHIPMENT_FEATURES = [
  'FBA Shipments', 'Warehouse Transfers', 'Marketplace Transfers',
  'Courier Planning', 'Freight Planning', 'Tracking',
];

export const WAREHOUSE_FEATURES = [
  'Multiple Warehouses', 'Inbound', 'Outbound', 'Capacity', 'Transfers', 'Cycle Counts', 'Damaged Goods',
];

export const FBA_METRICS = [
  'FBA Inventory', 'Inbound Shipments', 'Receiving', 'Reserved Inventory',
  'Stranded Inventory', 'Unfulfillable Inventory', 'Removal Orders', 'Capacity Utilization',
];

export const OPTIMIZATION_RECS = [
  'Advertising Changes', 'Inventory Transfers', 'Production Changes', 'Liquidation',
  'Promotions', 'Bundling', 'Marketplace Allocation',
];

export const CASH_FLOW_METRICS = [
  'Working Capital', 'Supplier Payments', 'Inventory ROI', 'Cash Flow', 'Funding Needs',
];

export const ADVISOR_CAPABILITIES = [
  'Demand Planning', 'Inventory Planning', 'Procurement Planning', 'Warehouse Planning',
  'FBA Planning', 'Cash Flow Planning', 'Business Recommendations',
];

export const COLLABORATION_FEATURES = [
  'Approvals', 'Assignments', 'Mentions', 'Supplier Discussions', 'Decision History',
];

export const ANALYTICS_METRICS = [
  'Forecast Accuracy', 'Inventory Turns', 'Days of Cover', 'Supplier Performance',
  'Warehouse Efficiency', 'Inventory ROI', 'Business Impact',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Inventory Command Center', 'Demand Forecasting', 'Replenishment Planning', 'Procurement',
  'Supplier Intelligence', 'Manufacturing', 'Shipment Management', 'Warehouse Management',
  'FBA Command Center', 'Inventory Optimization', 'Cash Flow Intelligence', 'AI Supply Chain Advisor',
];

export const LIVE_ROUTES: Record<string, string> = {
  inventoryIntel: '/amazon/inventory-intel',
  fbaOps: '/amazon/fba-ops',
  inventory: '/amazon/inventory',
};

export const DEMO_KPIS: CommandCenterKPI[] = [
  { label: 'Inventory Health', value: '87%', change: '+3pp', positive: true },
  { label: 'Available Stock', value: '24,842', change: '-2%', positive: false },
  { label: 'Reserved', value: '3,128', change: '+5%', positive: false },
  { label: 'Inbound', value: '8,450', change: '+12%', positive: true },
  { label: 'Transfer', value: '1,240', change: 'in transit', positive: true },
  { label: 'Damaged', value: '86', change: '-18%', positive: true },
  { label: 'Aging (>90d)', value: '412 units', change: '-8%', positive: true },
  { label: 'Inventory Value', value: '₹1.82Cr', change: '+4%', positive: false },
  { label: 'Working Capital', value: '₹68.4L', change: '-3%', positive: true },
  { label: 'Days of Cover', value: '42 days', change: 'optimal', positive: true },
  { label: 'Storage Fees', value: '₹2.1L/mo', change: '-6%', positive: true },
];

export const DEMO_FORECASTS: ForecastRow[] = [
  { horizon: '7d', units: 1840, change: '+6%' },
  { horizon: '15d', units: 3920, change: '+8%' },
  { horizon: '30d', units: 8420, change: '+11%' },
  { horizon: '60d', units: 16840, change: '+9%' },
  { horizon: '90d', units: 25400, change: '+7%' },
];

export const DEMO_REPLENISHMENTS: ReplenishmentRow[] = [
  { sku: 'SW-EARBUDS-PRO', qty: 2400, daysLeft: 12, supplier: 'Shenzhen Audio Co.', roi: '3.2x' },
  { sku: 'SW-HUB-GEN3', qty: 800, daysLeft: 8, supplier: 'Guangzhou IoT Ltd.', roi: '2.8x' },
  { sku: 'SW-YOGA-MAT-6MM', qty: 1200, daysLeft: 18, supplier: 'Mumbai Wellness Mfg.', roi: '2.1x' },
];

export const DEMO_PURCHASE_ORDERS: PurchaseOrderRow[] = [
  { po: 'PO-2026-0842', supplier: 'Shenzhen Audio Co.', items: 2400, value: '₹18.4L', status: 'approved' },
  { po: 'PO-2026-0841', supplier: 'Guangzhou IoT Ltd.', items: 800, value: '₹12.2L', status: 'pending_approval' },
  { po: 'PO-2026-0840', supplier: 'Mumbai Wellness Mfg.', items: 1200, value: '₹4.8L', status: 'in_transit' },
];

export const DEMO_FBA: FBASnapshotRow[] = [
  { metric: 'FBA Inventory', value: '18,420 units', alert: false },
  { metric: 'Inbound Shipments', value: '3 active · 8,450 units', alert: false },
  { metric: 'Receiving', value: '2,140 units today', alert: false },
  { metric: 'Stranded Inventory', value: '24 units', alert: true },
  { metric: 'Unfulfillable', value: '86 units', alert: true },
  { metric: 'Capacity Utilization', value: '72%', alert: false },
];

export const DEMO_WAREHOUSES: WarehouseRow[] = [
  { name: 'Mumbai WH-01', capacity: 10000, used: 7200, inbound: 840, outbound: 420 },
  { name: 'Delhi WH-02', capacity: 8000, used: 5100, inbound: 320, outbound: 680 },
  { name: 'Bangalore WH-03', capacity: 6000, used: 3800, inbound: 180, outbound: 240 },
];

export const DEMO_OPTIMIZATIONS: OptimizationRec[] = [
  { type: 'Inventory Transfers', title: 'Transfer 400 units Earbuds Pro from Delhi to FBA BLR8', impact: 'Prevent 6-day stockout', confidence: 92 },
  { type: 'Advertising Changes', title: 'Reduce ad spend on Yoga Mat — 28 days cover exceeds target', impact: 'Save ₹12K/mo ad waste', confidence: 88 },
  { type: 'Liquidation', title: 'Liquidate 86 damaged Hub Gen 2 units via removal order', impact: 'Recover ₹1.8L working capital', confidence: 95 },
  { type: 'Production Changes', title: 'Expedite PO-2026-0841 — lead time risk on Hub Gen 3', impact: 'Avoid ₹4.2L revenue loss', confidence: 84 },
  { type: 'Marketplace Allocation', title: 'Shift 15% inventory allocation to Amazon.com — higher velocity', impact: '+18% inventory turns', confidence: 79 },
];

export const DEMO_CASH_FLOW: CashFlowRow[] = [
  { label: 'Working Capital', current: '₹68.4L', forecast: '₹62.1L' },
  { label: 'Supplier Payments (30d)', current: '₹34.2L', forecast: '₹28.6L' },
  { label: 'Inventory ROI', current: '2.4x', forecast: '2.7x' },
  { label: 'Cash Flow', current: '₹12.8L', forecast: '₹15.2L' },
  { label: 'Funding Needs', current: 'None', forecast: '₹8L optional' },
];

export function buildSupplyChainDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    forecast_horizons: FORECAST_HORIZONS.length,
    replenishment_features: REPLENISHMENT_FEATURES.length,
    procurement_features: PROCUREMENT_FEATURES.length,
    fba_metrics: FBA_METRICS.length,
    optimization_recs: OPTIMIZATION_RECS.length,
  };
}
